import { FiLink2 } from 'react-icons/fi';

export const simpleLinkingWordsData = {
  courseId: 'simple-linking-words',
  courseType: 'writing-course',
  title: 'Simple Linking Words for Writing',
  icon: FiLink2,
  overview: {
    impact: 'Connect ideas smoothly within and between paragraphs',
    focus: 'Addition, contrast, cause/effect, sequence, and example linkers',
    uses: 'Writing Tasks 1 & 2 — Coherence and Cohesion',
    duration: '25-30 minutes',
  },
  sections: [
    {
      title: 'Addition and Sequence Linkers',
      content: `<p>Addition linkers let you add extra points that support the same idea. Sequence linkers organise your argument in a logical order.</p>`,
      table: {
        headers: ['Category', 'Linking Words', 'Position in Sentence'],
        rows: [
          ['Addition', 'Furthermore, Moreover, In addition, Additionally, Also', 'Start of sentence + comma'],
          ['Sequence (first)', 'Firstly, To begin with, First of all', 'Start of sentence + comma'],
          ['Sequence (middle)', 'Secondly, Next, Subsequently', 'Start of sentence + comma'],
          ['Sequence (last)', 'Finally, Lastly, In conclusion', 'Start of sentence + comma'],
        ],
      },
      points: [
        '<strong>Furthermore</strong> and <strong>Moreover</strong> are more formal than "Also" — prefer them in IELTS essays',
        'Do not overuse "Firstly, Secondly, Thirdly" — vary your linking words',
        '"In addition" can start a sentence or appear mid-sentence: "The policy, in addition, reduces costs"',
      ],
      examples: [
        {
          label: 'Addition',
          text: 'Public transport reduces pollution. Furthermore, it is more cost-effective for commuters than private vehicles.',
        },
        {
          label: 'Sequence',
          text: 'Firstly, the government should raise awareness of healthy eating. Secondly, schools should provide nutritious meals. Finally, food companies should be required to label products clearly.',
        },
      ],
    },
    {
      title: 'Contrast Linkers',
      content: `<p>Contrast linkers signal that the next idea <strong>opposes or qualifies</strong> the previous one. They are essential for discussion essays and balanced arguments.</p>`,
      table: {
        headers: ['Linker', 'Strength', 'Example'],
        rows: [
          ['However,', 'Strong contrast', 'The plan is ambitious. However, it may not be practical.'],
          ['Nevertheless,', 'Strong — despite what was said', 'The costs are high. Nevertheless, the benefits justify the expense.'],
          ['On the other hand,', 'Balanced contrast', 'Some prefer city life. On the other hand, others value rural tranquillity.'],
          ['Although / Even though', 'Within one sentence', 'Although the economy grew, unemployment remained high.'],
          ['While / Whereas', 'Direct comparison', 'While men preferred sport, women favoured reading.'],
        ],
      },
      points: [
        '"However" is the most versatile contrast linker — it works in almost any context',
        '"Although" and "While" join two clauses in ONE sentence — do not use them to start a standalone sentence followed by a full stop',
        'Avoid "But" at the start of sentences in formal IELTS writing — use "However" instead',
      ],
      warning: 'Common mistake: "However" requires a full sentence before and after it, separated by a full stop or semicolon. WRONG: "However the weather was bad." CORRECT: "The weather was bad. However, the event continued."',
    },
    {
      title: 'Cause, Effect, and Example Linkers',
      content: `<p>These linkers explain <strong>why</strong> something happens (cause), <strong>what happens as a result</strong> (effect), or give <strong>specific illustrations</strong> (example).</p>`,
      table: {
        headers: ['Category', 'Linking Words', 'Usage Note'],
        rows: [
          ['Cause', 'Because, Since, Due to, As a result of, Owing to', '"Due to" + noun phrase; "Because" + clause'],
          ['Effect', 'Therefore, Consequently, As a result, Thus, Hence', 'Start of sentence + comma, or mid-sentence'],
          ['Example', 'For instance, For example, Such as, Namely', '"Such as" introduces a list; "For example" introduces a sentence'],
        ],
      },
      points: [
        '<strong>Therefore</strong> and <strong>Consequently</strong> are ideal for showing results in formal writing',
        '"Due to" is followed by a noun phrase: "Due to the rain" (not "Due to it rained")',
        '"Such as" is used within a sentence: "Countries such as Japan and Germany…" — do not put a comma before "such as" when it is essential to the meaning',
        '"For instance" and "For example" are interchangeable and can start a new sentence',
      ],
      examples: [
        {
          label: 'Cause and effect',
          text: 'Many people lack access to clean water. Consequently, waterborne diseases remain a major health concern in developing nations.',
        },
        {
          label: 'Example',
          text: 'Several countries have implemented successful recycling programmes. For instance, Sweden recycles nearly 99% of its household waste.',
        },
      ],
    },
    {
      title: 'Common Mistakes with Linking Words',
      content: `<p>Using linking words incorrectly can <strong>lower your Coherence and Cohesion score</strong>. Watch for these frequent errors:</p>`,
      points: [
        '<strong>Overuse</strong> — starting every sentence with a linker makes writing mechanical. Use them only where the logical connection is not already clear.',
        '<strong>Wrong category</strong> — using "Furthermore" (addition) when you mean "However" (contrast)',
        '<strong>Punctuation errors</strong> — "Therefore" at the start of a sentence needs a comma after it',
        '<strong>Informal linkers in formal writing</strong> — avoid "Plus", "So", "Besides" in IELTS essays',
        '<strong>Double linking</strong> — "But however" or "And moreover" is redundant. Use one linker, not two.',
      ],
      examples: [
        {
          label: 'Overused linking words (poor)',
          text: 'Firstly, education is important. Moreover, it helps people find jobs. In addition, it improves society. Furthermore, it reduces crime.',
        },
        {
          label: 'Better version (natural)',
          text: 'Education is fundamental to individual and societal progress. It equips people with the skills needed to secure employment and, in turn, contributes to lower crime rates.',
        },
      ],
    },
  ],
  exercises: [
    {
      title: 'Categorise the Linking Word',
      type: 'select',
      instructions: 'Identify the category each linking word belongs to.',
      questions: [
        {
          text: '"Furthermore"',
          options: ['Addition', 'Contrast', 'Cause/Effect', 'Example'],
          answer: 'Addition',
          explanation: '"Furthermore" adds an extra point to support the same idea.',
        },
        {
          text: '"Nevertheless"',
          options: ['Contrast', 'Addition', 'Sequence', 'Example'],
          answer: 'Contrast',
          explanation: '"Nevertheless" means "despite what was just said" — it introduces a contrasting point.',
        },
        {
          text: '"Consequently"',
          options: ['Cause/Effect', 'Addition', 'Contrast', 'Sequence'],
          answer: 'Cause/Effect',
          explanation: '"Consequently" introduces a result or effect of what was stated before.',
        },
        {
          text: '"For instance"',
          options: ['Example', 'Sequence', 'Cause/Effect', 'Contrast'],
          answer: 'Example',
          explanation: '"For instance" introduces a specific illustration of the point being made.',
        },
      ],
    },
    {
      title: 'Fill in the Correct Linker',
      type: 'fill-in',
      instructions: 'Choose the most appropriate linking word for each gap.',
      questions: [
        {
          text: 'The cost of living has risen sharply. ___, many young people cannot afford to buy homes.',
          answer: 'Consequently',
          explanation: 'The second sentence is a result of the first, so a cause/effect linker is needed.',
        },
        {
          text: 'Electric cars produce zero emissions. ___, they require expensive batteries that are difficult to recycle.',
          answer: 'However',
          explanation: 'The second sentence contrasts with the positive point in the first, so a contrast linker is needed.',
        },
        {
          text: 'Regular exercise improves physical health. ___, it has been shown to reduce symptoms of anxiety and depression.',
          answer: 'Furthermore',
          explanation: 'The second sentence adds another benefit, so an addition linker is needed.',
        },
        {
          text: 'Some nations have made great progress in renewable energy. ___, Denmark generates over 50% of its electricity from wind power.',
          answer: 'For example',
          explanation: 'Denmark is a specific illustration, so an example linker is needed.',
        },
      ],
    },
    {
      title: 'Spot the Misused Linker',
      type: 'select',
      instructions: 'Identify which sentence uses a linking word incorrectly.',
      questions: [
        {
          text: 'Which sentence misuses a linking word?',
          options: [
            '"The weather was cold. Moreover, we decided to cancel the picnic."',
            '"The weather was cold. Therefore, we decided to cancel the picnic."',
            '"The weather was cold. However, we still enjoyed the day indoors."',
          ],
          answer: '"The weather was cold. Moreover, we decided to cancel the picnic."',
          explanation: '"Moreover" (addition) is wrong here because cancelling the picnic is a consequence, not an additional fact. "Therefore" (cause/effect) is correct.',
        },
        {
          text: 'Which sentence misuses a linking word?',
          options: [
            '"Education improves employment prospects. But however, not everyone has access to schooling."',
            '"Education improves employment prospects. However, not everyone has access to schooling."',
            '"Education improves employment prospects. Nevertheless, inequality in access remains a challenge."',
          ],
          answer: '"Education improves employment prospects. But however, not everyone has access to schooling."',
          explanation: '"But however" is double linking — redundant. Use either "But" or "However", never both.',
        },
        {
          text: 'Which sentence misuses a linking word?',
          options: [
            '"Due to it rained heavily, the match was postponed."',
            '"Due to heavy rain, the match was postponed."',
            '"Because it rained heavily, the match was postponed."',
          ],
          answer: '"Due to it rained heavily, the match was postponed."',
          explanation: '"Due to" must be followed by a noun phrase, not a clause. Use "Because" with a clause or "Due to" with a noun.',
        },
      ],
    },
    {
      title: 'Rewrite Using Linking Words',
      type: 'writing',
      instructions: 'Combine or rewrite the sentences using an appropriate linking word from the categories studied.',
      questions: [
        {
          text: 'Rewrite with a contrast linker: "The city invested heavily in public transport. Traffic congestion did not improve."',
          example: 'The city invested heavily in public transport. However, traffic congestion did not improve.',
          tip: 'Use "However", "Nevertheless", or "Despite this" to show the unexpected result.',
        },
        {
          text: 'Rewrite with an addition linker: "Smoking causes lung cancer. Smoking increases the risk of heart disease."',
          example: 'Smoking causes lung cancer. Furthermore, it increases the risk of heart disease.',
          tip: 'Use "Furthermore", "Moreover", or "In addition" to add the second health risk.',
        },
        {
          text: 'Rewrite with a cause/effect linker: "Many forests have been destroyed. Biodiversity has declined rapidly."',
          example: 'Many forests have been destroyed. As a result, biodiversity has declined rapidly.',
          tip: 'Use "As a result", "Therefore", or "Consequently" to show the effect.',
        },
      ],
    },
    {
      title: 'Complete the Paragraph',
      type: 'fill-in',
      instructions: 'Fill in the blanks with appropriate linking words to complete a coherent paragraph.',
      questions: [
        {
          text: 'There are several reasons why people choose to live in cities. ___, cities offer a wider range of employment opportunities than rural areas.',
          answer: 'Firstly',
          explanation: 'A sequence linker is needed to introduce the first reason.',
        },
        {
          text: '___, urban areas provide better access to healthcare and education facilities.',
          answer: 'Secondly',
          explanation: 'Continuing the sequence of reasons requires the next ordinal linker.',
        },
        {
          text: '___, city life also has disadvantages, such as higher living costs and pollution.',
          answer: 'However',
          explanation: 'A contrast linker introduces the opposing side of the argument.',
        },
        {
          text: '___, the decision to live in a city depends on each individual\'s priorities and circumstances.',
          answer: 'Therefore',
          explanation: 'A cause/effect linker draws the concluding point from the discussion.',
        },
      ],
    },
  ],
  tips: [
    'Learn linking words in categories (addition, contrast, cause/effect, example, sequence) — do not memorise random lists',
    'Use a variety of linkers — repeating "However" three times in one essay sounds unnatural',
    'Only use a linking word when the logical connection between sentences is not already obvious',
    'Check punctuation: most sentence-starting linkers are followed by a comma',
  ],
  dailyPractice: [
    'Read an IELTS model essay and highlight every linking word — categorise each one',
    'Rewrite three pairs of simple sentences by joining them with linking words',
    'Write a short paragraph (4-5 sentences) using at least three different categories of linker',
    'Practise replacing informal linkers (so, but, plus) with formal ones (therefore, however, furthermore)',
  ],
  backPath: '/exams/english/#writing',
  backLabel: 'Back to Writing',
  aiContext: 'Simple Linking Words for Writing: Connecting ideas using addition, contrast, cause/effect, sequence, and example linkers to improve coherence and cohesion in IELTS Writing Tasks 1 and 2.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...simpleLinkingWordsData,
      title: simpleLinkingWordsData.title + ' (Foundations)',
      overview: { ...simpleLinkingWordsData.overview, duration: '15-20 minutes' },
      sections: simpleLinkingWordsData.sections.slice(0, Math.max(2, Math.ceil(simpleLinkingWordsData.sections.length / 2))),
      exercises: simpleLinkingWordsData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...simpleLinkingWordsData,
      title: simpleLinkingWordsData.title + ' (Advanced)',
      overview: { ...simpleLinkingWordsData.overview, duration: '30-35 minutes' },
      sections: simpleLinkingWordsData.sections,
      exercises: simpleLinkingWordsData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(simpleLinkingWordsData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return simpleLinkingWordsData;
}
